import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import MortgageService from '../../services/MortgageService';
import { Card, Input, InputGroup, FormSection, Select, toast, MoneyInput } from '../../components/v2/V2Components';
import { sanitizeNumeric } from '../../utils/numericInput';
import { mortgageMaturityDate } from '../../utils/dateHelpers';
import usePageView from '../../hooks/usePageView';
import { ANALYTICS_EVENTS } from '../../constants/analyticsEvents';

const LOAN_TYPES = ['Conventional', 'FHA', 'VA', 'HELOC', 'Other'];

// Hides the native number-input spinner (up/down steppers) in WebKit + Firefox.
const NO_SPINNER =
  '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none';

const initialFormState = {
  id: null,
  lender: '',
  loan_type: '',
  account_last4: '',
  interest_rate: '',
  original_loan_amount: '',
  origination_date: '',
  maturity_date: '',
  term_months: '',
  has_escrow: 'true',
  pmi_monthly: '',
  notes: '',
};

const MortgageForm = () => {
  const { houseId, mortgageId } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!mortgageId;

  usePageView(ANALYTICS_EVENTS.MORTGAGE_FORM_VIEWED, { is_edit_mode: isEditMode });

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(initialFormState);

  useEffect(() => {
    const fetchMortgage = async () => {
      try {
        const m = await MortgageService.getMortgage(mortgageId);
        setFormData({
          id: m.id,
          lender: m.lender || '',
          loan_type: m.loan_type || '',
          account_last4: m.account_last4 || '',
          interest_rate: m.interest_rate ?? '',
          original_loan_amount: m.original_loan_amount ?? '',
          origination_date: m.origination_date || '',
          maturity_date: m.maturity_date || '',
          term_months: m.term_months ?? '',
          has_escrow: m.has_escrow === false ? 'false' : 'true',
          pmi_monthly: m.pmi_monthly ?? '',
          notes: m.notes || '',
        });
      } catch (error) {
        toast.error?.('Failed to load mortgage');
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    if (isEditMode) fetchMortgage();
  }, [mortgageId, isEditMode]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      // Derive maturity from origination + term (term-month loan on the 1st).
      // Still editable afterward; only re-derived when those two inputs change.
      if (name === 'origination_date' || name === 'term_months') {
        // Re-derive maturity; clear it when the inputs no longer yield one so a
        // stale derived value can't linger in the payload after a field is cleared.
        next.maturity_date = mortgageMaturityDate(next.origination_date, next.term_months) || '';
      }
      return next;
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.lender.trim()) {
      toast.error?.('Lender is required');
      return;
    }
    // Fail closed: if the edit-mode load failed, formData.id is null and a save
    // would PUT to /mortgages/null. Block it and tell the user to retry.
    if (isEditMode && !formData.id) {
      toast.error?.('This mortgage could not be loaded. Reload the page and try again.');
      return;
    }
    try {
      setSaving(true);
      const termRaw = sanitizeNumeric(formData.term_months);
      const payload = {
        lender: formData.lender.trim(),
        loan_type: formData.loan_type || null,
        account_last4: formData.account_last4 || null,
        interest_rate: sanitizeNumeric(formData.interest_rate),
        original_loan_amount: sanitizeNumeric(formData.original_loan_amount),
        origination_date: formData.origination_date || null,
        maturity_date: formData.maturity_date || null,
        term_months: termRaw == null ? null : Math.round(termRaw),
        has_escrow: formData.has_escrow === 'true',
        pmi_monthly: sanitizeNumeric(formData.pmi_monthly),
        notes: formData.notes || null,
      };

      if (isEditMode) {
        await MortgageService.updateMortgage(formData.id, payload);
        toast.success?.('Mortgage updated');
        navigate(`/houses/${houseId}/mortgages/${formData.id}`);
      } else {
        const created = await MortgageService.createMortgage(houseId, payload);
        toast.success?.('Mortgage added');
        // Land on the new mortgage so the user can immediately add statements.
        navigate(`/houses/${houseId}/mortgages/${created.id}`);
      }
    } catch (error) {
      toast.error?.(error.message || 'Failed to save mortgage');
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <Loader2 className="animate-spin text-[var(--primary)]" size={48} />
      </div>
    );

  return (
    <div className="animate-fade-in max-w-4xl mx-auto pb-10">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[var(--text-main)] mb-1">
            {isEditMode ? 'Edit Mortgage' : 'Add Mortgage'}
          </h1>
          <p className="text-[var(--text-secondary)]">
            {isEditMode ? 'Update your loan details' : 'Add a loan for this property'}
          </p>
        </div>
        {/* gap, not space-x: space-x-* uses child margins, which break across
            the rows this group can now wrap onto. */}
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() =>
              navigate(isEditMode ? `/houses/${houseId}/mortgages/${mortgageId}` : `/houses/${houseId}?tab=mortgage`)
            }
            className="px-4 py-2 border border-[var(--border)] text-[var(--text-secondary)] rounded-lg hover:bg-[var(--bg-hover)] transition-colors"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="mortgage-form"
            disabled={saving}
            className="flex items-center space-x-2 px-6 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition-all font-medium disabled:opacity-70"
          >
            {saving && <Loader2 size={16} className="animate-spin" />}
            <span>{isEditMode ? 'Save Changes' : 'Create Mortgage'}</span>
          </button>
        </div>
      </div>

      <Card className="p-8">
        <form id="mortgage-form" onSubmit={handleSave} className="space-y-8">
          <FormSection title="Loan Details">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputGroup label="Lender / Servicer" required>
                <Input name="lender" value={formData.lender} onChange={handleInputChange} required />
              </InputGroup>
              <InputGroup label="Loan Type">
                <Select name="loan_type" value={formData.loan_type} onChange={handleInputChange}>
                  <option value="">Select…</option>
                  {LOAN_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </Select>
              </InputGroup>
              <InputGroup label="Account (last 4)">
                <Input
                  name="account_last4"
                  value={formData.account_last4}
                  onChange={handleInputChange}
                  maxLength={4}
                  inputMode="numeric"
                  placeholder="1234"
                />
              </InputGroup>
              <InputGroup label="Interest Rate (%)">
                <Input
                  className={NO_SPINNER}
                  name="interest_rate"
                  type="number"
                  step="0.001"
                  value={formData.interest_rate}
                  onChange={handleInputChange}
                />
              </InputGroup>
              <InputGroup label="Original Loan Amount">
                <MoneyInput
                  name="original_loan_amount"
                  value={formData.original_loan_amount}
                  onChange={handleInputChange}
                />
              </InputGroup>
              <InputGroup label="Term (months)">
                <Input className={NO_SPINNER} name="term_months" type="number" value={formData.term_months} onChange={handleInputChange} />
              </InputGroup>
              <InputGroup label="Origination Date">
                <Input
                  name="origination_date"
                  type="date"
                  value={formData.origination_date}
                  onChange={handleInputChange}
                />
              </InputGroup>
              <InputGroup label="Maturity Date">
                <Input name="maturity_date" type="date" value={formData.maturity_date} onChange={handleInputChange} />
              </InputGroup>
              <InputGroup label="Escrow Account">
                <Select name="has_escrow" value={formData.has_escrow} onChange={handleInputChange}>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </Select>
              </InputGroup>
              <InputGroup label="Monthly PMI">
                <MoneyInput
                  name="pmi_monthly"
                  value={formData.pmi_monthly}
                  onChange={handleInputChange}
                />
              </InputGroup>
            </div>
          </FormSection>

          <FormSection title="Notes">
            <InputGroup label="Notes">
              <Input name="notes" value={formData.notes} onChange={handleInputChange} placeholder="Optional" />
            </InputGroup>
          </FormSection>
        </form>
      </Card>
    </div>
  );
};

export default MortgageForm;
